import {Action, ActionReducer, createReducer, on} from '@ngrx/store';
import {getDeepCopyOfObject} from 'src/app/shared/shared-utils/object/object.utils';
import * as UP_Actions from './user-device.actions';
import {
  DEFAULT_DEVICE_ORIENTATION,
  DEFAULT_DEVICE_PERMISSIONS,
  DEFAULT_DEVICE_USER_AGENT,
  IDeviceOrientation,
  IDevicePermissions,
  IDeviceUserAgent,
} from '../models';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {EDevicePermissionState} from '../models/device-permissions.enum';

export interface IUserDeviceState {
  errors: IGlobalError[];
  orientation: IDeviceOrientation;
  permissions: IDevicePermissions;
  userAgent: IDeviceUserAgent;
}

export const initialUserDeviceState: IUserDeviceState = {
  errors: [],
  orientation: <IDeviceOrientation>{...DEFAULT_DEVICE_ORIENTATION},
  permissions: <IDevicePermissions>{...DEFAULT_DEVICE_PERMISSIONS},
  userAgent: <IDeviceUserAgent>{...DEFAULT_DEVICE_USER_AGENT},
};

const createUserReducer: ActionReducer<IUserDeviceState> = createReducer(
  getDeepCopyOfObject(initialUserDeviceState),
  //#region [USER DEVICE][GLOBAL]
  on(UP_Actions.clearUserPreferencesState, (state: IUserDeviceState) => ({
      ...getDeepCopyOfObject(initialUserDeviceState),
    }),
  ),
  //#endregion
  //#region [USER DEVICE][DEVICE] Get User Device Orientation
  on(UP_Actions.getUserDeviceOrientation, (state: IUserDeviceState, {orientation}) => {
    return {
      ...state,
      orientation: <IDeviceOrientation>{
        ...state.orientation,
        isDeviceOrientationLoading: false,
        orientation: orientation,
      },
    };
  }),
  on(UP_Actions.getUserDeviceOrientationAngles, (state: IUserDeviceState, {angle}) => {
    return {
      ...state,
      orientation: <IDeviceOrientation>{
        ...state.orientation,
        isDeviceOrientationLoading: false,
        deviceAngles: angle,
      },
    };
  }),
  on(UP_Actions.getUserScreenOrientationAngles, (state: IUserDeviceState, {angle}) => {
    return {
      ...state,
      orientation: <IDeviceOrientation>{
        ...state.orientation,
        isDeviceOrientationLoading: false,
        screenAngle: angle,
      },
    };
  }),
  //#endregion
  //#region [USER DEVICE][DEVICE] Get User Device User Agent
  on(UP_Actions.getUserDeviceUserAgent, (state: IUserDeviceState) => {
    return {
      ...state,
      userAgent: <IDeviceUserAgent>{
        ...state.userAgent,
        isDeviceUserAgentLoading: true,
      },
    };
  }),
  on(UP_Actions.getUserDeviceUserAgentSuccess, (state: IUserDeviceState, {userAgent}) => {
    return {
      ...state,
      userAgent: <IDeviceUserAgent>{
        ...state.userAgent,
        ...userAgent,
        isDeviceUserAgentLoading: false,
      },
    };
  }),
  on(UP_Actions.getUserDeviceUserAgentFailure, (state: IUserDeviceState, {error}) => {
    return {
      ...state,
      errors: [
        ...state.errors,
        error,
      ],
      userAgent: <IDeviceUserAgent>{
        ...state.userAgent,
        isDeviceUserAgentLoading: false,
      },
    };
  }),
  //#endregion
  //#region [USER DEVICE][PERMISSIONS] Get User Device Permission
  on(UP_Actions.getUserDevicePermission, (state: IUserDeviceState, {permission}) => {
    return {
      ...state,
      permissions: <IDevicePermissions>{
        ...state.permissions,
        isDevicePermissionLoading: true,
        [permission]: EDevicePermissionState.prompt,
      },
    };
  }),
  on(UP_Actions.getUserDevicePermissionSuccess, (state: IUserDeviceState, {permission, access}) => {
    return {
      ...state,
      permissions: <IDevicePermissions>{
        ...state.permissions,
        isDevicePermissionLoading: false,
        [permission]: access,
      },
    };
  }),
  on(UP_Actions.getUserDevicePermissionFailure, (state: IUserDeviceState, {access, error, permission}) => {
    return {
      ...state,
      errors: [
        ...state.errors,
        error,
      ],
      permissions: <IDevicePermissions>{
        ...state.permissions,
        isDevicePermissionLoading: false,
        [permission]: EDevicePermissionState.prompt,
      },
    };
  }),
  //#endregion
  //#region [USER DEVICE][PERMISSIONS] Get User Device Permissions
  on(UP_Actions.getUserDevicePermissions, (state: IUserDeviceState) => {
    return {
      ...state,
      permissions: {
        ...state.permissions,
        isDevicePermissionLoading: true,
      }
    };
  }),
  on(UP_Actions.getUserDevicePermissionsSuccess, (state: IUserDeviceState, {permissions}) => {
    return {
      ...state,
      permissions: {
        ...state.permissions,
        ...permissions,
        isDevicePermissionLoading: true,
      }
    };
  }),
  on(UP_Actions.getUserDevicePermissionsFailure, (state: IUserDeviceState, {error}) => {
    return {
      ...state,
      errors: [
        ...state.errors,
        error,
      ],
      permissions: {
        ...DEFAULT_DEVICE_PERMISSIONS,
        isDevicePermissionLoading: false,
      },
    };
  }),
  //#endregion
);
export const userDeviceReducer = (state: IUserDeviceState, action: Action): IUserDeviceState =>
  createUserReducer(state, action);
